import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
  ProcessedRoute,
  RoutePlayerState,
  RouteFilter,
  RouteComparisonData,
  RoutePoint,
  SelectedPointInfo,
} from "../types/route";
import { apiService } from "../services/api";
import { RouteProcessor } from "../utils/routeUtils";
import { useBikeStore } from "./useBikeStore";
import { useVehicleCategoryStore } from "./useVehicleCategoryStore";

function resolveVehicleMaxSpeedKmh(bikeId: string): number | null {
  const bike = useBikeStore.getState().bikes.find((b) => b.bike_id === bikeId);
  if (!bike) return null;

  if (bike.max_speed != null && bike.max_speed > 0) {
    return bike.max_speed;
  }

  if (!bike.category_id) return null;

  const category = useVehicleCategoryStore
    .getState()
    .categories.find((c) => c.category_id === bike.category_id);
  if (category?.max_speed_kmh != null && category.max_speed_kmh > 0) {
    return category.max_speed_kmh;
  }

  return null;
}

const initialPlayerState: RoutePlayerState = {
  isPlaying: false,
  isPaused: false,
  currentIndex: 0,
  playbackSpeed: 1,
  currentTime: "",
  progress: 0,
};

interface RouteState {
  // Route data
  routes: ProcessedRoute[];
  selectedRoute: ProcessedRoute | null;
  comparisonData: RouteComparisonData | null;

  // Player state
  playerState: RoutePlayerState;

  // Filters and settings
  filters: RouteFilter;
  showStops: boolean;
  showSegments: boolean;
  showGpsPoints: boolean;
  colorBySpeed: boolean;
  selectedPointInfo: SelectedPointInfo | null;

  // Loading states
  loadingRoutes: boolean;
  loadingComparison: boolean;

  // Actions
  fetchRoute: (
    bikeId: string,
    startTime: string,
    endTime: string,
  ) => Promise<void>;
  fetchMultipleRoutes: (
    bikeIds: string[],
    startTime: string,
    endTime: string,
  ) => Promise<void>;
  selectRoute: (route: ProcessedRoute | null) => void;
  updateFilters: (filters: Partial<RouteFilter>) => void;

  // Player actions
  playRoute: () => void;
  pauseRoute: () => void;
  stopRoute: () => void;
  seekTo: (progress: number) => void;
  seekToFrame: (frameIndex: number) => void;
  setPlaybackSpeed: (speed: number) => void;

  clearComparison: () => void;

  // Settings
  setShowStops: (show: boolean) => void;
  setShowSegments: (show: boolean) => void;
  setShowGpsPoints: (show: boolean) => void;
  setColorBySpeed: (color: boolean) => void;
  setSelectedPointInfo: (info: SelectedPointInfo | null) => void;
}

function getPlaybackFrameCount(route: ProcessedRoute | null): number {
  if (!route) return 0;
  return RouteProcessor.buildPlaybackFrames(route).length;
}

export const useRouteStore = create<RouteState>()(
  devtools(
    (set, get) => ({
      // Initial state
      routes: [],
      selectedRoute: null,
      comparisonData: null,
      playerState: initialPlayerState,
      filters: {
        dateRange: {
          start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
          end: new Date().toISOString(),
        },
      },
      showStops: true,
      showSegments: true,
      showGpsPoints: false,
      colorBySpeed: true,
      selectedPointInfo: null,
      loadingRoutes: false,
      loadingComparison: false,

      // Actions
      fetchRoute: async (bikeId, startTime, endTime) => {
        set({ loadingRoutes: true });
        try {
          const routeData = (await apiService.getMapRoute(bikeId, {
            start_time: startTime,
            end_time: endTime,
            max_points: 5000,
          })) as { coordinates: RoutePoint[] };

          const processedRoute = RouteProcessor.processRoute(
            bikeId,
            routeData.coordinates,
            { maxSpeedKmh: resolveVehicleMaxSpeedKmh(bikeId) },
          );

          set({
            routes: processedRoute ? [processedRoute] : [],
            selectedRoute: processedRoute,
            selectedPointInfo: null,
            playerState: initialPlayerState,
            loadingRoutes: false,
          });
        } catch (error) {
          console.error("Error fetching route:", error);
          set({ loadingRoutes: false });
        }
      },

      fetchMultipleRoutes: async (bikeIds, startTime, endTime) => {
        set({ loadingRoutes: true });
        try {
          const routePromises = bikeIds.map((bikeId) =>
            apiService
              .getMapRoute(bikeId, {
                start_time: startTime,
                end_time: endTime,
                max_points: 5000,
              })
              .then((data) =>
                RouteProcessor.processRoute(
                  bikeId,
                  (data as { coordinates: RoutePoint[] }).coordinates,
                  { maxSpeedKmh: resolveVehicleMaxSpeedKmh(bikeId) },
                ),
              ),
          );

          const routes = (await Promise.all(routePromises)).filter(
            (route): route is ProcessedRoute => route != null,
          );

          set({
            routes: routes.filter((route) => route.coordinates.length > 0),
            selectedPointInfo: null,
            playerState: initialPlayerState,
            loadingRoutes: false,
          });
        } catch (error) {
          console.error("Error fetching multiple routes:", error);
          set({ loadingRoutes: false });
        }
      },

      selectRoute: (route) => {
        get().stopRoute();
        set({
          selectedRoute: route,
          selectedPointInfo: null,
          playerState: initialPlayerState,
        });
      },

      updateFilters: (newFilters) => {
        set({
          filters: { ...get().filters, ...newFilters },
        });
      },

      // Player actions
      playRoute: () => {
        const { selectedRoute, playerState } = get();
        if (!selectedRoute) return;

        set({
          playerState: {
            ...playerState,
            isPlaying: true,
            isPaused: false,
          },
        });
      },

      pauseRoute: () => {
        const { playerState } = get();
        set({
          playerState: {
            ...playerState,
            isPlaying: false,
            isPaused: true,
          },
        });
      },

      stopRoute: () => {
        set({ playerState: initialPlayerState });
      },

      seekTo: (progress) => {
        const { selectedRoute, playerState } = get();
        if (!selectedRoute) return;

        const frameCount = getPlaybackFrameCount(selectedRoute);
        if (frameCount === 0) return;

        const newIndex = Math.min(
          frameCount - 1,
          Math.max(0, Math.floor((progress / 100) * (frameCount - 1))),
        );
        const frames = RouteProcessor.buildPlaybackFrames(selectedRoute);
        const frame = frames[newIndex];
        const position = frame ? RouteProcessor.getFramePosition(frame) : null;

        set({
          playerState: {
            ...playerState,
            currentIndex: newIndex,
            progress,
            currentTime: position?.timestamp ?? "",
            isPlaying: false,
            isPaused: true,
          },
        });
      },

      seekToFrame: (frameIndex) => {
        const { selectedRoute } = get();
        if (!selectedRoute) return;

        const frameCount = getPlaybackFrameCount(selectedRoute);
        if (frameCount === 0) return;

        const progress =
          frameCount > 1 ? (frameIndex / (frameCount - 1)) * 100 : 0;
        get().seekTo(progress);
      },

      setPlaybackSpeed: (speed) => {
        const { playerState } = get();
        set({
          playerState: {
            ...playerState,
            playbackSpeed: speed,
          },
        });
      },

      clearComparison: () => {
        set({ comparisonData: null });
      },

      // Settings
      setShowStops: (show) => set({ showStops: show }),
      setShowSegments: (show) => set({ showSegments: show }),
      setShowGpsPoints: (show) => set({ showGpsPoints: show }),
      setColorBySpeed: (color) => set({ colorBySpeed: color }),
      setSelectedPointInfo: (info) => set({ selectedPointInfo: info }),
    }),
    { name: "route-store" },
  ),
);
